/**
 * Favorites and lists - phase 13.
 *
 * A master-detail split, the same shape as the registry (section 18's
 * .master-detail): favorites and every named list down the left, the
 * selected one's companies on the right. Favorites is a fixed pseudo-list
 * pinned first - it is the one basket that always exists and can never be
 * renamed or deleted, unlike everything below it.
 *
 * The selected list lives in the URL (`?list=favorites` or `?list=3`), the
 * same reasoning as the registry's `?company=` - a specific list's contents
 * should be bookmarkable and survive a refresh.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  IconAlert,
  IconCheck,
  IconClose,
  IconEdit,
  IconPlus,
  IconStar,
  IconTrash,
} from '../components/Icons.jsx';
import { listsApi } from '../lib/api.js';
import { useFavorites } from '../lib/favorites-context.jsx';
import { useLists } from '../lib/lists-context.jsx';

function formatDate(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function DeleteListDialog({ list, busy, error, onCancel, onConfirm }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel]);

  return (
    <div className="scrim" onClick={busy ? undefined : onCancel} role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-list-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dialog-header">
          <div>
            <h2 className="dialog-title" id="delete-list-title">
              Delete "{list.name}"?
            </h2>
            <p className="dialog-description">
              Removes this list and forgets which {list.itemCount} compan
              {list.itemCount === 1 ? 'y was' : 'ies were'} on it. The companies themselves stay in
              the registry - only the list is deleted. This cannot be undone.
            </p>
          </div>
        </div>
        {error ? (
          <div className="dialog-body">
            <div className="alert alert-error">
              <span className="alert-icon">
                <IconAlert />
              </span>
              <span className="alert-body">{error}</span>
            </div>
          </div>
        ) : null}
        <div className="dialog-footer">
          <span className="dialog-footer-spacer" />
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={busy}>
            {busy && <span className="spinner" />}
            {busy ? 'Deleting' : 'Delete list'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** One company card, shared by the Favorites pane and every list pane. */
function CompanyCard({ item, onRemove, removing }) {
  const company = item.company;
  return (
    <div className="list-company-card">
      <div className="list-company-card-body">
        <span className="list-company-card-name">{company?.name ?? item.companyNumber}</span>
        <span className="list-company-card-meta">
          <Link className="mono" to={`/registry?company=${item.companyNumber}`}>
            {item.companyNumber}
          </Link>
          {company?.statusLabel ? (
            <span className={`badge badge-sm ${company.status === 'active' ? 'badge-ok' : 'badge-unknown'}`}>
              <span className="badge-dot" />
              {company.statusLabel}
            </span>
          ) : company === null ? (
            <span className="badge badge-sm badge-unknown">No longer in the registry</span>
          ) : null}
          {company?.locality ? <span>{company.locality}</span> : null}
          <span className="text-faint">added {formatDate(item.addedAt)}</span>
        </span>
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-icon btn-sm tip tip-left"
        onClick={onRemove}
        disabled={removing}
        data-tip="Remove"
        aria-label={`Remove ${company?.name ?? item.companyNumber}`}
      >
        {removing ? <span className="spinner" /> : <IconTrash />}
      </button>
    </div>
  );
}

export default function ListsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get('list') || 'favorites';

  const favorites = useFavorites();
  const { lists, isPending, createList, renameList, deleteList, removeFromList } = useLists();

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState(null);

  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const requestId = useRef(0);

  const selectedListId = selected === 'favorites' ? null : Number(selected);

  const loadDetail = useCallback(async (id) => {
    const thisRequest = ++requestId.current;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const data = await listsApi.get(id);
      if (requestId.current === thisRequest) setDetail(data);
    } catch (caught) {
      if (requestId.current === thisRequest) setDetailError(caught.message);
    } finally {
      if (requestId.current === thisRequest) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedListId === null) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedListId);
  }, [selectedListId, loadDetail]);

  const setSelected = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set('list', String(value));
    setSearchParams(next, { replace: true });
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreateError(null);
    try {
      const created = await createList(name);
      setNewName('');
      setCreating(false);
      setSelected(created.id);
    } catch (caught) {
      setCreateError(caught.message);
    }
  };

  const startRename = (list) => {
    setRenamingId(list.id);
    setRenameValue(list.name);
  };

  const commitRename = async (event, list) => {
    event.preventDefault();
    const name = renameValue.trim();
    if (!name || name === list.name) {
      setRenamingId(null);
      return;
    }
    await renameList(list.id, name);
    setRenamingId(null);
    if (selectedListId === list.id) void loadDetail(list.id);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteList(deleteTarget.id);
      if (selectedListId === deleteTarget.id) setSelected('favorites');
      setDeleteTarget(null);
    } catch (caught) {
      setDeleteError(caught.message);
    } finally {
      setDeleting(false);
    }
  };

  const activeList = selectedListId === null ? null : lists.find((list) => list.id === selectedListId);
  const title = selectedListId === null ? 'Favorites' : (activeList?.name ?? detail?.name ?? 'List');
  const items = selectedListId === null ? favorites.items : (detail?.items ?? []);
  const loading = selectedListId === null ? favorites.isLoading : detailLoading;
  const error = selectedListId === null ? favorites.error : detailError;

  return (
    <div className="page">
      <div className="page-content page-content-full">
        <div className="page-header">
          <div>
            <h1 className="page-title">Favorites and lists</h1>
            <p className="page-description">
              Star a company from the registry to favorite it, or drop it into a named list. Create
              as many lists as you like and rename them at any time.
            </p>
          </div>
        </div>

        <div className="master-detail" style={{ height: 640 }}>
          <div className="master">
            <div className="master-list">
              <button
                type="button"
                className={`list-nav-item${selected === 'favorites' ? ' is-selected' : ''}`}
                onClick={() => setSelected('favorites')}
              >
                <IconStar filled />
                <span className="list-nav-item-name">Favorites</span>
                <span className="list-nav-item-count">{favorites.count}</span>
              </button>

              {lists.map((list) =>
                renamingId === list.id ? (
                  <form
                    key={list.id}
                    className="list-rename-form"
                    onSubmit={(event) => void commitRename(event, list)}
                  >
                    <input
                      type="text"
                      className="input"
                      value={renameValue}
                      onChange={(event) => setRenameValue(event.target.value)}
                      maxLength={120}
                      autoFocus
                      onBlur={() => setRenamingId(null)}
                    />
                    <button type="submit" className="btn btn-ghost btn-icon btn-sm" aria-label="Save name">
                      <IconCheck />
                    </button>
                  </form>
                ) : (
                  <div
                    key={list.id}
                    className={`list-nav-item${selectedListId === list.id ? ' is-selected' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelected(list.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') setSelected(list.id);
                    }}
                  >
                    <span className="list-nav-item-name">{list.name}</span>
                    <span className="list-nav-item-count">{list.itemCount}</span>
                    <span className="list-nav-item-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon btn-sm tip tip-below"
                        onClick={(event) => {
                          event.stopPropagation();
                          startRename(list);
                        }}
                        data-tip="Rename"
                        aria-label={`Rename ${list.name}`}
                      >
                        <IconEdit />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon btn-sm tip tip-below"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleteError(null);
                          setDeleteTarget(list);
                        }}
                        disabled={isPending(`list:${list.id}`)}
                        data-tip="Delete list"
                        aria-label={`Delete ${list.name}`}
                      >
                        <IconTrash />
                      </button>
                    </span>
                  </div>
                ),
              )}

              {creating ? (
                <form className="list-nav-new" onSubmit={handleCreate}>
                  <input
                    type="text"
                    className="input"
                    placeholder="List name"
                    value={newName}
                    onChange={(event) => setNewName(event.target.value)}
                    maxLength={120}
                    autoFocus
                  />
                  <button type="submit" className="btn btn-primary btn-sm" disabled={!newName.trim()}>
                    <IconCheck />
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setCreating(false);
                      setNewName('');
                    }}
                  >
                    <IconClose />
                  </button>
                </form>
              ) : (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreating(true)}>
                  <IconPlus />
                  New list
                </button>
              )}

              {createError ? (
                <div className="alert alert-error">
                  <span className="alert-body">{createError}</span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="master">
            <div className="detail-header">
              <div className="row row-between">
                <h2 className="profile-name">{title}</h2>
                <span className="text-xs text-faint">
                  {items.length} compan{items.length === 1 ? 'y' : 'ies'}
                </span>
              </div>
            </div>

            <div className="master-list">
              {error ? (
                <div className="alert alert-error">
                  <span className="alert-icon">
                    <IconAlert />
                  </span>
                  <span className="alert-body">{error}</span>
                </div>
              ) : loading ? (
                <div className="loading-panel">
                  <span className="spinner" />
                  Loading
                </div>
              ) : items.length === 0 ? (
                <div className="empty">
                  <h3 className="empty-title">
                    {selectedListId === null ? 'Nothing favorited yet' : 'This list is empty'}
                  </h3>
                  <p className="empty-description">
                    {selectedListId === null
                      ? 'Press the star on a company in the registry to favorite it.'
                      : 'Add companies to this list from the registry or a company\'s profile.'}
                  </p>
                </div>
              ) : (
                items.map((item) => (
                  <CompanyCard
                    key={item.companyNumber}
                    item={item}
                    removing={
                      selectedListId === null
                        ? favorites.isPending(item.companyNumber)
                        : isPending(`list:${selectedListId}:${item.companyNumber}`)
                    }
                    onRemove={async () => {
                      if (selectedListId === null) {
                        await favorites.remove(item.companyNumber);
                      } else {
                        await removeFromList(selectedListId, item.companyNumber);
                        void loadDetail(selectedListId);
                      }
                    }}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {deleteTarget ? (
        <DeleteListDialog
          list={deleteTarget}
          busy={deleting}
          error={deleteError}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDelete()}
        />
      ) : null}
    </div>
  );
}
